import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Send, Sparkles } from "lucide-react";

export default function RefundModal({ leak, onClose, onDone }) {
  const [tab, setTab] = useState(leak.platform === "apple" ? "apple" : "merchant_email");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");

  const generate = async (channel) => {
    setLoading(true);
    try {
      const { data } = await api.post("/refunds/request", { leak_id: leak.id, channel });
      setDraft(data.draft_email);
      toast.success("Refund draft ready. Tracker is watching your inbox.");
    } catch (e) {
      if (e?.response?.status !== 402) toast.error("Failed to draft refund");
    } finally { setLoading(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft || "");
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — select the text to copy it");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#141618] border-[#282C30] text-[#F4F1EA] max-w-2xl" data-testid="refund-modal">
        <DialogHeader>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">REFUND BOT</div>
          <DialogTitle className="font-display text-2xl tracking-tight">Get back ${leak.amount.toFixed(2)} from {leak.merchant}</DialogTitle>
          <DialogDescription className="text-[#A0A7B0]">
            Pick your channel. We draft it. You approve. We track the credit.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="bg-[#0C0D0E] border border-[#282C30]" data-testid="refund-tabs">
            <TabsTrigger value="apple" data-testid="refund-tab-apple">Apple petition</TabsTrigger>
            <TabsTrigger value="merchant_email" data-testid="refund-tab-merchant">Merchant email</TabsTrigger>
            <TabsTrigger value="bank_dispute" data-testid="refund-tab-bank">Bank dispute letter</TabsTrigger>
          </TabsList>

          {["apple", "merchant_email", "bank_dispute"].map((k) => (
            <TabsContent key={k} value={k} className="mt-4">
              {!draft || tab !== k ? (
                <div className="text-center py-8 space-y-4">
                  <p className="text-sm text-[#A0A7B0]">
                    {k === "apple" && "Files an 'I didn't intend to renew' petition at reportaproblem.apple.com."}
                    {k === "merchant_email" && "Drafts a polite but firm refund request to the merchant's billing team."}
                    {k === "bank_dispute" && "Formal chargeback letter under Reg E / Reg Z. Send to your bank."}
                  </p>
                  <Button className="btn-emerald" onClick={() => generate(k)} disabled={loading} data-testid={`refund-generate-${k}`}>
                    <Sparkles className="h-4 w-4 mr-1.5" /> {loading ? "Drafting…" : "Draft with AI"}
                  </Button>
                </div>
              ) : (
                <>
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={12}
                    className="bg-[#0C0D0E] border-[#282C30] font-mono text-sm"
                    data-testid="refund-draft-textarea"
                  />
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" onClick={copy} className="btn-outline-paper" data-testid="refund-copy-button">
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                    <Button className="btn-emerald flex-1" onClick={() => { toast.success("Tracking refund for 14 days"); onDone(); }} data-testid="refund-send-button">
                      <Send className="h-4 w-4 mr-1" /> I sent it — start tracking
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
